import { reportDraftDb } from "../report-draft/reportDraftDb";
import { drainAssistantQueue } from "./draftDrain";
import {
	appendTurnEvent,
	connectionSnapshot,
	flushBufferedEvents,
	recordCancelledTurn,
	setDrainTrigger,
} from "./turnEvents";

/**
 * turnKernel — captura offline + fila idempotente do turno de análise (M-A).
 * Reusa os stores IndexedDB do BEL-1578 (photos + sync_queue) e acrescenta
 * o store assistant_turns. As fotos vão direto ao S3 pelos endpoints do
 * assistente (offline-photos/presign|confirm); o turno fecha-se com offline-turns.
 *
 * Dupla porta de batch (cliente): o turno só é POSTado quando TODAS as suas
 * fotos estão confirmadas em S3 (op photo_upload removida da fila).
 */
export class TurnKernel {
	constructor({ apiBaseFor, csrfToken = null } = {}) {
		// apiBaseFor(visitId) → `/admin/currentVisit/${visitId}/assistant`
		this.apiBaseFor = apiBaseFor;
		this.csrfToken = csrfToken;
	}

	/** Captura uma foto para o turno corrente (ainda aberto). */
	async capturePhoto(visitId, turnUuid, blob, meta = {}) {
		const photoUuid = crypto.randomUUID();
		await reportDraftDb.putPhoto({ photoUuid, visitId, turnUuid, kind: "assistant", blob, meta, syncState: "pending" });
		await reportDraftDb.enqueue({ type: "photo_upload", kind: "assistant", visitId, turnUuid, photoUuid });
		await appendTurnEvent(turnUuid, "captured", {
			photoUuid,
			bytes: blob && typeof blob.size === "number" ? blob.size : null,
			...connectionSnapshot(),
		});
		return photoUuid;
	}

	/** Fecha o turno: grava o manifesto + enfileira o POST do turno. */
	async closeTurn(visitId, photoUuids, { text = null, geo = null, deviceMeta = null } = {}) {
		const turnUuid = crypto.randomUUID();
		return this.closeTurnWithUuid(turnUuid, visitId, photoUuids, { text, geo, deviceMeta });
	}

	/**
	 * Variante de closeTurn com turnUuid explícito — para o mixin (P4.3) gerar
	 * o turnUuid no momento da captura, passá-lo a capturePhoto (tag das fotos)
	 * e fechar o MESMO turno aqui, garantindo um único turnUuid por turno.
	 */
	async closeTurnWithUuid(turnUuid, visitId, photoUuids, { text = null, geo = null, deviceMeta = null } = {}) {
		await reportDraftDb.putTurn({
			turnUuid,
			visitId,
			photoUuids,
			text,
			geo,
			deviceMeta,
			capturedAt: new Date().toISOString(),
			syncState: "pending",
			// Eventos de capturePhoto, que correu antes de este registo existir.
			events: flushBufferedEvents(turnUuid),
		});
		await appendTurnEvent(turnUuid, "queued", connectionSnapshot());
		await reportDraftDb.enqueue({ type: "assistant_turn", kind: "assistant", visitId, turnUuid });

		// Background Sync (Android): pede ao SW para acordar quando a rede voltar
		// — mesmo com a tab em segundo plano — e notificar os clients (o autosync
		// drena ambas as filas em qualquer página). Reusa a tag existente do SW.
		// iOS não suporta: os gatilhos focus/visibility/online/heartbeat cobrem.
		if (typeof navigator !== "undefined" && "serviceWorker" in navigator && "SyncManager" in window) {
			try {
				const reg = await navigator.serviceWorker.ready;
				await reg.sync.register("bel-report-draft-sync");
			} catch (_e) {
				// best-effort — sem Background Sync, os outros gatilhos drenam.
			}
		}

		return turnUuid;
	}

	/**
	 * Apaga por completo um turno LOCAL — fotos, ops em fila e manifesto — para
	 * cancelar uma captura que ainda só existe no dispositivo (paridade com o
	 * delete da cadeia online). Idempotente: seguro se o turno já não existir.
	 *
	 * Só atua em turnos AINDA NÃO sincronizados. Se o turno já tem taskId
	 * (aceite pelo servidor / na cadeia), devolve false sem apagar nada — esse
	 * é o caminho online e não deve passar por aqui.
	 *
	 * @returns {Promise<boolean>} true se removeu um turno local; false caso contrário.
	 */
	async deleteTurn(turnUuid) {
		if (!turnUuid) {
			return false;
		}
		const turn = await reportDraftDb.getTurn(turnUuid);
		if (turn && turn.taskId) {
			return false; // já sincronizado — não é um delete local.
		}

		const photoUuids = (turn && Array.isArray(turn.photoUuids) ? turn.photoUuids : []).slice();
		for (const photoUuid of photoUuids) {
			await reportDraftDb.deletePhoto(photoUuid);
			await reportDraftDb.dequeueByPhoto(photoUuid);
		}

		if (typeof reportDraftDb.dequeueByTurn === "function") {
			await reportDraftDb.dequeueByTurn(turnUuid);
		} else {
			const queue = await reportDraftDb.allQueue();
			for (const op of queue) {
				if (op.type === "assistant_turn" && op.turnUuid === turnUuid) {
					await reportDraftDb.dequeue(op.id);
				}
			}
		}

		await recordCancelledTurn(turn);
		await reportDraftDb.deleteTurn(turnUuid);
		return true;
	}

	/**
	 * Sincroniza a fila do assistente: fotos primeiro, turnos pela ordem de
	 * captura. Reusa a política de erro do kernel (4xx dead-letter, 5xx retry).
	 * Devolve o nº de ops ainda pendentes.
	 */
	async syncAll(opts = {}) {
		// Coalesce concurrent drains onto a single in-flight promise (prevents the
		// double turn POST → orphan user message). But MERGE each caller's
		// onAuthError so a foreground submitTurnForeground that coalesces onto a
		// background drain still learns about a 419/401 (else it misclassifies as
		// "pending" and the session modal never shows).
		if (this._syncing) {
			if (opts.onAuthError) {
				this._authErrorListeners = this._authErrorListeners || [];
				this._authErrorListeners.push(opts.onAuthError);
			}
			return this._syncing;
		}
		this._authErrorListeners = opts.onAuthError ? [opts.onAuthError] : [];
		const mergedOpts = {
			...opts,
			onAuthError: () => this._authErrorListeners.forEach(cb => cb()),
		};
		this._syncing = this._syncAllOnce(mergedOpts);
		try {
			return await this._syncing;
		} finally {
			this._syncing = null;
			this._authErrorListeners = [];
		}
	}

	async _syncAllOnce(opts = {}) {
		return drainAssistantQueue({
			apiBaseFor: this.apiBaseFor,
			getCsrf: () => this.csrfToken,
			...opts,
		});
	}

	/**
	 * Recuperação manual de um turno falhado (needs_attention, ou o legacy
	 * dead_letter): repõe as fotos falhadas a pending (zera o contador de PUT
	 * S3), re-enfileira as ops em falta e devolve o turno a pending. O chamador
	 * dispara depois o drain (runPullResume/syncAll). Devolve false para turnos
	 * já sincronizados, inexistentes ou ainda pendentes — nada a recuperar.
	 */
	async retryTurn(turnUuid) {
		const turn = await reportDraftDb.getTurn(turnUuid);
		if (!turn || turn.taskId || !["needs_attention", "dead_letter"].includes(turn.syncState)) {
			return false;
		}

		const queue = await reportDraftDb.allQueue();
		for (const photoUuid of Array.isArray(turn.photoUuids) ? turn.photoUuids : []) {
			const photo = await reportDraftDb.getPhoto(photoUuid);
			if (!photo) {
				continue; // já confirmada em S3 e apagada localmente
			}
			if (photo.syncState !== "pending" || photo.s3Attempts) {
				photo.syncState = "pending";
				delete photo.s3Attempts;
				await reportDraftDb.putPhoto(photo);
			}
			if (!queue.some(op => op.type === "photo_upload" && op.photoUuid === photoUuid)) {
				await reportDraftDb.enqueue({
					type: "photo_upload",
					kind: "assistant",
					visitId: turn.visitId,
					turnUuid,
					photoUuid,
				});
			}
		}

		turn.syncState = "pending";
		await reportDraftDb.putTurn(turn);
		if (!queue.some(op => op.type === "assistant_turn" && op.turnUuid === turnUuid)) {
			await reportDraftDb.enqueue({ type: "assistant_turn", kind: "assistant", visitId: turn.visitId, turnUuid });
		}
		return true;
	}

	async confirmedTurns(visitId) {
		return (await reportDraftDb.allTurns()).filter(
			t => String(t.visitId) === String(visitId) && t.syncState === "confirmed"
		);
	}

	/**
	 * Caminho ONLINE (M4): dreja a fila em foreground e classifica o resultado
	 * para ESTE turno. Reusa o mesmo motor do offline (`syncAll`), só que aqui o
	 * chamador (mixin) espera e arranca o polling com o task_id devolvido.
	 *
	 * @returns {Promise<{status: 'confirmed'|'auth'|'failed'|'pending', taskId?: number, userMessageId?: number|null}>}
	 */
	async submitTurnForeground(turnUuid) {
		setDrainTrigger("capture");
		let authStalled = false;
		await this.syncAll({
			onAuthError: () => {
				authStalled = true;
			},
		});

		const turn = await reportDraftDb.getTurn(turnUuid);
		if (turn && turn.syncState === "confirmed" && turn.taskId) {
			return { status: "confirmed", taskId: turn.taskId, userMessageId: turn.userMessageId ?? null };
		}
		if (authStalled) {
			return { status: "auth" };
		}
		if (turn && (turn.syncState === "dead_letter" || turn.syncState === "needs_attention")) {
			return { status: "failed" };
		}
		return { status: "pending" };
	}
}
